import React, { useState, useEffect, useRef, useCallback } from "react";
import { AiOutlinePlus, AiOutlineClose } from "react-icons/ai";
import {
  getOperatorsForField as getOperators,
  getInputCount as getCount,
  getFieldControl,
  findField,
  groupFields,
} from "../config/searchFields";

/**
 * FilterBuilder - Componente para construir filtros dinámicos
 *
 * Permite al usuario agregar/eliminar filtros, seleccionar campos, operadores y valores.
 * Soporta diferentes tipos de operadores según el tipo de campo.
 */
const FilterBuilder = ({ metadataSpecs, onFiltersChange, initialFilters = [], onSuggest }) => {
  const [filters, setFilters] = useState(initialFilters);
  const [suggestions, setSuggestions] = useState({});
  const debounceTimers = useRef({});

  const fetchSuggestions = useCallback((filterId, fieldName, value) => {
    if (!onSuggest || !fieldName || !value || value.length < 2) {
      setSuggestions(prev => ({ ...prev, [filterId]: [] }));
      return;
    }
    if (debounceTimers.current[filterId]) clearTimeout(debounceTimers.current[filterId]);
    debounceTimers.current[filterId] = setTimeout(async () => {
      try {
        const results = await onSuggest(fieldName, value);
        setSuggestions(prev => ({ ...prev, [filterId]: results || [] }));
      } catch { setSuggestions(prev => ({ ...prev, [filterId]: [] })); }
    }, 300);
  }, [onSuggest]);

  // Generar ID único para cada filtro
  const generateId = () => `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Operadores, tipos de input y controles viven en config/searchFields.js, compartidos
  // con AdvancedFilterBuilder para que un tipo de campo nuevo se agregue en un solo lugar.
  const getOperatorsForField = (fieldName) => getOperators(metadataSpecs, fieldName);
  const getInputCount = (fieldName, operatorValue) => getCount(metadataSpecs, fieldName, operatorValue);

  // Agregar nuevo filtro
  const handleAddFilter = () => {
    const firstField = metadataSpecs[0];
    if (!firstField) return;

    const operators = getOperatorsForField(firstField.name);
    const defaultOperator = operators[0]?.value || 'term';

    const newFilter = {
      id: generateId(),
      fieldName: firstField.name,
      operator: defaultOperator,
      value: '',
      value2: '' // Para operadores de rango
    };

    const updatedFilters = [...filters, newFilter];
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  // Eliminar filtro
  const handleRemoveFilter = (filterId) => {
    const updatedFilters = filters.filter(f => f.id !== filterId);
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  // Actualizar campo de un filtro
  const handleFieldChange = (filterId, newFieldName) => {
    const operators = getOperatorsForField(newFieldName);
    const defaultOperator = operators[0]?.value || 'term';

    const updatedFilters = filters.map(f => 
      f.id === filterId 
        ? { ...f, fieldName: newFieldName, operator: defaultOperator, value: '', value2: '' }
        : f
    );
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  // Actualizar operador de un filtro
  const handleOperatorChange = (filterId, newOperator) => {
    const updatedFilters = filters.map(f => 
      f.id === filterId 
        ? { ...f, operator: newOperator, value: '', value2: '' }
        : f
    );
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  // Actualizar valor de un filtro
  const handleValueChange = (filterId, valueKey, newValue, fieldName, fieldType) => {
    const updatedFilters = filters.map(f =>
      f.id === filterId
        ? { ...f, [valueKey]: newValue }
        : f
    );
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
    if (valueKey === 'value' && fieldType === 'STRING') {
      fetchSuggestions(filterId, fieldName, newValue);
    }
  };

  // Sincronizar con initialFilters cuando cambia externamente
  useEffect(() => {
    if (initialFilters.length === 0 && filters.length > 0) {
      setFilters([]);
    }
  }, [initialFilters]);

  return (
    <div style={styles.container}>
      {/* Tabla de filtros */}
      {filters.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Campo</th>
              <th style={styles.th}>Operador</th>
              <th style={styles.th}>Valor</th>
              <th style={styles.thActions}></th>
            </tr>
          </thead>
          <tbody>
            {filters.map((filter) => {
              const control = getFieldControl(metadataSpecs, filter.fieldName);
              const inputType = control.type;
              const inputCount = getInputCount(filter.fieldName, filter.operator);
              const operators = getOperatorsForField(filter.fieldName);
              const fieldSpec = findField(metadataSpecs, filter.fieldName);
              const fieldType = fieldSpec?.type || '';

              return (
                <tr key={filter.id} style={styles.row}>
                  {/* Campo */}
                  <td style={styles.td}>
                    <select
                      value={filter.fieldName}
                      onChange={(e) => handleFieldChange(filter.id, e.target.value)}
                      style={styles.select}
                    >
                      {groupFields(metadataSpecs).map(group => (
                        <optgroup key={group.label} label={group.label}>
                          {group.fields.map(field => (
                            <option key={field.name} value={field.name}>
                              {field.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </td>

                  {/* Operador */}
                  <td style={styles.td}>
                    <select
                      value={filter.operator}
                      onChange={(e) => handleOperatorChange(filter.id, e.target.value)}
                      style={styles.select}
                    >
                      {operators.map(op => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Valor(es) */}
                  <td style={styles.td}>
                    <div style={styles.valueContainer}>
                      {control.kind === 'select' ? (
                        <select
                          value={filter.value}
                          onChange={(e) => handleValueChange(filter.id, 'value', e.target.value, filter.fieldName, fieldType)}
                          style={styles.select}
                          aria-label="Valor"
                        >
                          <option value="">Seleccioná…</option>
                          {control.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={inputType}
                          list={fieldType === 'STRING' ? `suggest-${filter.id}` : undefined}
                          value={filter.value}
                          onChange={(e) => handleValueChange(filter.id, 'value', e.target.value, filter.fieldName, fieldType)}
                          style={styles.input}
                          placeholder={inputCount === 2 ? "Desde" : "Valor"}
                          autoComplete="off"
                        />
                      )}
                      {fieldType === 'STRING' && suggestions[filter.id]?.length > 0 && (
                        <datalist id={`suggest-${filter.id}`}>
                          {suggestions[filter.id].map(s => <option key={s} value={s} />)}
                        </datalist>
                      )}
                      {inputCount === 2 && (
                        <>
                          <span style={styles.separator}>a</span>
                          <input
                            type={inputType}
                            value={filter.value2}
                            onChange={(e) => handleValueChange(filter.id, 'value2', e.target.value)}
                            style={styles.input}
                            placeholder="Hasta"
                          />
                        </>
                      )}
                    </div>
                  </td>

                  {/* Acciones */}
                  <td style={styles.tdActions}>
                    <button
                      onClick={() => handleRemoveFilter(filter.id)}
                      style={styles.removeButton}
                      title="Eliminar filtro"
                    >
                      <AiOutlineClose />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Botón agregar filtro */}
      <button
        onClick={handleAddFilter}
        style={styles.addButton}
        disabled={metadataSpecs.length === 0}
      >
        <AiOutlinePlus style={styles.buttonIcon} />
        Agregar Filtro
      </button>

      {/* Mensaje informativo */}
      {filters.length > 0 && (
        <div style={styles.infoMessage}>
          ℹ️ Los filtros se combinan con AND
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  th: {
    backgroundColor: '#00468B',
    color: '#fff',
    padding: '10px',
    textAlign: 'left',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  thActions: {
    backgroundColor: '#00468B',
    color: '#fff',
    padding: '10px',
    width: '50px',
  },
  row: {
    borderBottom: '1px solid #ddd'
  },
  td: {
    padding: '10px',
    verticalAlign: 'middle'
  },
  tdActions: {
    padding: '10px',
    textAlign: 'center',
    verticalAlign: 'middle'
  },
  select: {
    width: '100%',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '14px',
  },
  valueContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  input: {
    flex: 1,
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '14px',
  },
  separator: {
    color: '#666',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  removeButton: {
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 8px',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s'
  },
  addButton: {
    backgroundColor: '#00468B',
    color: '#fff',
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    alignSelf: 'flex-start',
    transition: 'background-color 0.2s'
  },
  buttonIcon: {
    fontSize: '16px'
  },
  infoMessage: {
    color: '#666',
    fontSize: '13px',
    fontStyle: 'italic',
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  }
};

export default FilterBuilder;
